<template>
  <div class="container-fluid pt-4 px-4">

    <!-- Data freshness. Shown first, and deliberately: a backtest run against
         stale candles looks exactly like one run against current data. -->
    <v-card flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4 d-flex align-center flex-wrap ga-4">
        <div>
          <div class="text-caption text-medium-emphasis">Candle data through</div>
          <div class="text-h6 font-weight-medium">{{ freshness.latestDate || '—' }}</div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">Stocks with candles that day</div>
          <div class="text-h6 font-weight-medium">{{ freshness.codesWithAnyCandles || 0 }}</div>
        </div>
        <!-- Captured and usable are different numbers: the runner skips any day
             below the full-session bar, so a day can be captured for 180 stocks
             and usable for far fewer. Backtesting against the first number
             while the second is what actually runs is how a thin result looks
             like a broad one. -->
        <div>
          <div class="text-caption text-medium-emphasis">
            Usable (&ge;{{ freshness.minCandlesForFullDay || 300 }} candles)
          </div>
          <div class="text-h6 font-weight-medium"
               :class="usableShortfall ? 'text-warning' : ''">
            {{ freshness.codesWithFullSession || 0 }}
          </div>
        </div>
        <v-chip v-if="freshnessWarning" size="small" variant="tonal" color="warning">
          {{ freshnessWarning }}
        </v-chip>
      </v-card-text>
    </v-card>

    <!-- Run a backtest -->
    <v-card flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-title class="px-5 py-4">
        <span class="text-h6 font-weight-medium">Run a backtest</span>
      </v-card-title>
      <v-card-text class="px-5 pb-5">
        <v-row dense>
          <v-col cols="12" md="3">
            <v-select
              v-model="form.strategyType" :items="strategyOptions" item-title="title" item-value="value"
              label="Strategy" variant="outlined" density="comfortable" hide-details />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field v-model="form.fromDate" label="From (YYYY-MM-DD)" variant="outlined" density="comfortable" hide-details />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field v-model="form.toDate" label="To (YYYY-MM-DD)" variant="outlined" density="comfortable" hide-details />
          </v-col>
          <!-- Live enters at most max_trades_per_day per strategy and fills those
               slots in arrival order, so it owns the EARLIEST signals of the day.
               Uncapped, the backtest takes every qualifying stock (52 on one day
               against a live cap of 1) and measures the AVERAGE signal instead.
               A switch rather than a silent correction: which of the two is more
               favourable is exactly what running it both ways is meant to settle. -->
          <v-col cols="12" md="3" class="d-flex align-center ga-2">
            <v-switch v-model="capEnabled" color="primary" density="compact"
                      hide-details label="Daily cap" />
            <v-text-field v-model.number="form.maxTradesPerDay" :disabled="!capEnabled"
                          type="number" min="1" label="Trades/day" variant="outlined"
                          density="comfortable" hide-details style="max-width: 110px" />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn color="primary" :loading="starting" :disabled="!form.strategyType" block @click="startRun">
              Run
            </v-btn>
          </v-col>
        </v-row>
        <v-row dense class="mt-1">
          <!-- Minutes after the open before an entry may fire. Tests whether the
               earliest signals are worth taking: bullish died at its original stop
               on 140 of 350 trades, and 82% of live entries fire in the first 45
               minutes. Overrides extra_config for THIS RUN ONLY -- the live
               strategy row is never written. -->
          <v-col cols="12" md="4" class="d-flex align-center ga-2">
            <v-switch v-model="guardEnabled" color="primary" density="compact"
                      hide-details label="Entry guard" />
            <v-text-field v-model.number="form.entryAfterMinutes" :disabled="!guardEnabled"
                          type="number" min="1" max="360" label="Wait (min)" variant="outlined"
                          density="comfortable" hide-details style="max-width: 120px" />
          </v-col>
        </v-row>
        <!-- Per-run overrides. Applied to THIS RUN ONLY and never written back to
             any settings row: testing whether a wider stage-1 lock or a fixed
             target helps must not change what trades tomorrow, which is the whole
             reason the Lab exists. -->
        <v-expansion-panels variant="accordion" class="mt-2">
          <v-expansion-panel elevation="0">
            <v-expansion-panel-title class="text-body-2">
              Override settings for this run
              <span v-if="activeOverrideCount" class="ml-2 text-caption text-primary">
                ({{ activeOverrideCount }} active)
              </span>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-select v-model="overrides.exit_strategy" :items="exitStrategyOptions"
                            label="Exit strategy" variant="outlined" density="comfortable"
                            clearable hide-details />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field v-model="overrides.stop_buffer_pct" type="number" step="0.1"
                                label="Stop buffer %" variant="outlined" density="comfortable"
                                clearable hide-details />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field v-model="overrides.risk_per_trade_rs" type="number"
                                label="Risk / trade (Rs)" variant="outlined" density="comfortable"
                                clearable hide-details />
                </v-col>
              </v-row>
              <!-- Stage 1 locks 20% of the move once price reaches 60% of it, so a
                   trade that runs to 1.2R and turns is banked at 0.4R. That is where
                   the momentum strategies give their winners back. -->
              <div class="text-caption text-medium-emphasis mt-4 mb-1">Trail rungs (trail_runner only)</div>
              <v-row dense>
                <v-col v-for="f in trailFields" :key="f.key" cols="6" md="2">
                  <v-text-field v-model="overrides[f.key]" type="number" :label="f.label"
                                variant="outlined" density="comfortable" clearable hide-details />
                </v-col>
              </v-row>
              <!-- Strategy-private knobs. Free-form because every strategy invents
                   its own: range_expansion_days here, target_rr_multiple there. -->
              <v-textarea v-model="extraConfigText" class="mt-4" rows="2" variant="outlined"
                          density="comfortable" hide-details
                          label="extra_config overrides (JSON)" />
              <div class="text-caption text-medium-emphasis mt-1">{{ extraConfigHint }}</div>
              <div v-if="extraConfigError" class="text-caption text-error mt-1">{{ extraConfigError }}</div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <div v-if="startError" class="text-caption text-error mt-2">{{ startError }}</div>
      </v-card-text>
    </v-card>

    <!-- Progress. An interrupted run shows a STOPPED bar: a bar still
         animating for a process that no longer exists is worse than none,
         because it manufactures confidence that work is happening. -->
    <v-card v-if="run && (run.presentedStatus === 'running' || run.presentedStatus === 'interrupted')"
            flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="text-subtitle-1">
            {{ run.presentedStatus === 'interrupted' ? 'Interrupted' : 'Running' }} — {{ run.strategy_type }}
          </span>
          <span class="text-caption text-medium-emphasis">
            {{ run.done_units || 0 }} / {{ run.total_units || '?' }}
            <template v-if="run.current_code"> · {{ run.current_code }}</template>
          </span>
        </div>
        <v-progress-linear
          :model-value="progressPct"
          :indeterminate="false"
          :color="run.presentedStatus === 'interrupted' ? 'warning' : 'primary'"
          height="18" rounded />
        <div v-if="run.presentedStatus === 'interrupted'" class="text-caption text-warning mt-2">
          The process appears to have stopped — no progress for a while. Nothing was corrupted;
          start a new run when ready.
        </div>
      </v-card-text>
    </v-card>

    <!-- Verdict -->
    <v-card v-if="verdict" flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4">
        <div class="d-flex align-center ga-3 mb-2">
          <v-chip size="large" variant="tonal" :color="verdictColor">{{ verdictLabel }}</v-chip>
          <span class="text-subtitle-1">{{ run.strategy_type }}</span>
        </div>
        <p class="text-body-2 mb-3">{{ verdict.reason }}</p>
        <v-row dense>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Cost hurdle</div>
            <div class="text-h6">{{ fmtR(verdict.hurdleR) }}</div>
            <div class="text-caption text-medium-emphasis">
              {{ hurdleBasisLabel }}
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">95% interval (by day)</div>
            <div class="text-h6" v-if="verdict.interval">
              {{ fmtR(verdict.interval.lower) }} … {{ fmtR(verdict.interval.upper) }}
            </div>
            <div class="text-h6" v-else>—</div>
          </v-col>
          <!-- Trades AND trading days, always together. 213 trades across 22
               days must never read like 213 independent observations. -->
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Trades</div>
            <div class="text-h6">{{ verdict.tradeCount }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Trading days</div>
            <div class="text-h6" :class="verdict.tradingDays < verdict.minTradingDays ? 'text-warning' : ''">
              {{ verdict.tradingDays }} <span class="text-caption">/ {{ verdict.minTradingDays }} needed</span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Headline stats -->
    <v-card v-if="overall && overall.tradeCount" flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4">
        <v-row dense>
          <v-col v-for="tile in statTiles" :key="tile.label" cols="6" md="2">
            <div class="text-caption text-medium-emphasis">{{ tile.label }}</div>
            <div class="text-h6" :class="tile.cls">{{ tile.value }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- How trades actually ended. summarise() has always computed this and
         the run row has always stored it; nothing displayed it, so the single
         most diagnostic table about the exit logic was invisible. In
         trail_runner there is no target exit at all -- reaching the target
         only ratchets the stop and extends the target -- so these three
         counts are the whole story of how money is made or lost:
         "stop" never ratcheted (the entry was wrong straight away),
         "trail_stop_hit" ratcheted then trailed out (a banked win),
         "eod_squareoff" never resolved either way (the signal went nowhere). -->
    <v-card v-if="exitRows.length" flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-title class="px-5 py-4">
        <span class="text-h6 font-weight-medium">How trades ended</span>
      </v-card-title>
      <v-card-text class="px-5 pb-5">
        <v-row dense>
          <v-col v-for="row in exitRows" :key="row.reason" cols="6" md="3">
            <div class="text-caption text-medium-emphasis">{{ row.label }}</div>
            <div class="text-h6">{{ row.count }} <span class="text-caption text-medium-emphasis">({{ row.pct }}%)</span></div>
          </v-col>
        </v-row>
        <div class="text-caption text-medium-emphasis mt-3">{{ exitNote }}</div>
      </v-card-text>
    </v-card>

    <EquityCurveChart v-if="dailyForChart.length" :daily="dailyForChart" title="Cumulative P&L by day" class="mb-4" />

    <!-- Per-day results -->
    <v-card v-if="days.length" flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-title class="px-5 py-4 d-flex align-center justify-space-between">
        <span class="text-h6 font-weight-medium">Per-day results</span>
        <v-btn v-if="run && run.status === 'completed'" size="small" variant="tonal"
               :loading="reviewing" @click="markReviewed">
          {{ run.reviewed_at ? 'Reviewed' : 'Mark reviewed' }}
        </v-btn>
      </v-card-title>
      <v-card-text class="px-5 pb-5">
        <div v-if="reviewMessage" class="text-caption text-success mb-2">{{ reviewMessage }}</div>
        <div class="table-scroll">
          <v-table density="compact" class="custom-data-table">
            <thead>
              <tr>
                <th>Date</th><th class="text-right">Trades</th><th class="text-right">W/L</th>
                <th class="text-right">Win %</th>
                <!-- These two are on DIFFERENT bases and must say so. expectancyRs is
                     NET of charges; expectancyR is GROSS. Unlabelled they read as a
                     contradiction -- 2026-09-02 showed -Rs 29.27 alongside +0.064 R,
                     both correct. The gross column is the one to compare against the
                     cost hurdle, since the hurdle IS the charges. -->
                <th class="text-right">Expectancy (net)</th>
                <th class="text-right">Exp R (gross)</th><th class="text-right">Charges</th><th class="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in days" :key="d.trade_date">
                <td>{{ d.trade_date }}</td>
                <td class="text-right">{{ d.trade_count }}</td>
                <td class="text-right">{{ d.win_count }}/{{ d.loss_count }}</td>
                <td class="text-right">{{ formatPct(d.win_rate_pct) }}</td>
                <td class="text-right">{{ formatMoney(d.expectancy_rs) }}</td>
                <td class="text-right">{{ fmtR(d.expectancy_r) }}</td>
                <td class="text-right">{{ formatMoney(d.charges_rs) }}</td>
                <td class="text-right" :class="deltaClass(d.net_pnl_rs)">{{ formatMoney(d.net_pnl_rs) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Run history. This is also what makes a newly-added strategy visible
         here first: it lists every run, so a strategy with none stands out. -->
    <v-card flat rounded="lg" class="gap-backtest-card">
      <v-card-title class="px-5 py-4">
        <span class="text-h6 font-weight-medium">Recent runs</span>
      </v-card-title>
      <v-card-text class="px-5 pb-5">
        <div class="table-scroll">
          <v-table density="compact" class="custom-data-table">
            <thead>
              <tr>
                <th>#</th><th>Strategy</th><th>Range</th><th>Status</th>
                <th class="text-right">Trades</th><th class="text-right">Cap</th><th class="text-right">Guard</th><th>Reviewed</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in runs" :key="r.strategy_lab_run_id">
                <td>{{ r.strategy_lab_run_id }}</td>
                <td>{{ r.strategy_type }}</td>
                <td class="text-caption">{{ r.from_date }} → {{ r.to_date }}</td>
                <td>
                  <v-chip size="x-small" variant="tonal" :color="statusColor(r.presentedStatus)">
                    {{ r.presentedStatus }}
                  </v-chip>
                </td>
                <td class="text-right">{{ r.overall_stats ? r.overall_stats.tradeCount : '—' }}</td>
                <td class="text-right">{{ capLabel(r) }}</td>
                <td class="text-right">{{ guardLabel(r) }}</td>
                <td class="text-caption">{{ r.reviewed_at ? 'yes' : '—' }}</td>
                <td class="text-right">
                  <v-btn size="x-small" variant="text" @click="viewRun(r.strategy_lab_run_id)">View</v-btn>
                </td>
              </tr>
              <tr v-if="!runs.length">
                <td colspan="9" class="py-6 text-center text-medium-emphasis">
                  No runs yet. Pick a strategy above and run one.
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card-text>
    </v-card>

  </div>
</template>

<script>
// Strategy Lab -- backtest a strategy before anyone enables it for live
// trading. A strategy appears here first; enabling it stays a separate,
// deliberate step on Strategy Config. Nothing on this page starts trading.
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import EquityCurveChart from './EquityCurveChart.vue';

export default {
  name: 'StrategyLab',
  components: { EquityCurveChart },
  data() {
    return {
      form: { strategyType: null, fromDate: '', toDate: '', maxTradesPerDay: 1, entryAfterMinutes: 15 },
      // OFF by default so the uncapped baseline stays the default experiment.
      // Whether live's earliest-first slots beat the average signal is unknown,
      // so neither setting is the 'correct' one -- they are two questions.
      capEnabled: false,
      // Also off by default. Both switches exist to be run BOTH ways; a
      // default-on guard would quietly make the baseline unreachable.
      guardEnabled: false,
      // null/blank means INHERIT, never zero. Number("") is 0 and finite, so
      // blanks are stripped before sending rather than coerced into a real 0 --
      // an override of 0 and "leave it alone" are very different experiments.
      overrides: {
        exit_strategy: null, stop_buffer_pct: null, risk_per_trade_rs: null,
        trail_stage1_trigger_pct: null, trail_stage1_lock_pct: null,
        trail_stage2_trigger_pct: null, trail_stage2_lock_pct: null,
        trail_target_lock_pct: null, trail_runner_extension_pct: null,
      },
      extraConfigText: "",
      extraConfigError: "",
      extraConfigHint: "Example: { \"range_expansion_days\": 3, \"target_rr_multiple\": 2 }",
      exitStrategyOptions: ["single_stage", "multi_stage", "trail_runner"],
      trailFields: [
        { key: "trail_stage1_trigger_pct", label: "S1 trigger %" },
        { key: "trail_stage1_lock_pct", label: "S1 lock %" },
        { key: "trail_stage2_trigger_pct", label: "S2 trigger %" },
        { key: "trail_stage2_lock_pct", label: "S2 lock %" },
        { key: "trail_target_lock_pct", label: "Target lock %" },
        { key: "trail_runner_extension_pct", label: "Extension %" },
      ],
      strategies: [],
      freshness: {},
      runs: [],
      run: null,
      verdict: null,
      overall: null,
      days: [],
      starting: false,
      reviewing: false,
      startError: null,
      reviewMessage: null,
      pollTimer: null,
    };
  },
  computed: {
    // Only implemented strategies. getStrategy() throws for a registered-but-
    // unimplemented type, so offering it would just produce a failed run.
    strategyOptions() {
      return this.strategies
        .filter((s) => s.implemented)
        .map((s) => ({ title: s.strategyType, value: s.strategyType }));
    },
    progressPct() {
      const total = Number(this.run && this.run.total_units);
      const done = Number(this.run && this.run.done_units);
      if (!Number.isFinite(total) || total <= 0) return 0;
      return Math.round((done / total) * 100);
    },
    verdictLabel() {
      return this.verdict ? this.verdict.verdict.replace(/_/g, ' ') : '';
    },
    verdictColor() {
      return { PASS: 'success', FAIL: 'error', NOT_ENOUGH_DATA: 'warning' }[this.verdict && this.verdict.verdict] || 'grey';
    },
    dailyForChart() {
      // EquityCurveChart takes { period, pnlRs } and cumulatively sums.
      return this.days.map((d) => ({ period: d.trade_date, pnlRs: Number(d.net_pnl_rs) || 0 }));
    },
    // Ordered worst-to-best rather than by count, so the reading order is the
    // diagnosis: died immediately, drifted, banked something.
    // Blank means inherit, so blanks are stripped here rather than sent as
    // nulls -- the API then sees only what was deliberately changed.
    cleanedOverrides() {
      const out = {};
      Object.entries(this.overrides).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") return;
        out[key] = value;
      });
      return out;
    },

    activeOverrideCount() {
      return Object.keys(this.cleanedOverrides).length + (this.extraConfigText.trim() ? 1 : 0);
    },

    // Says WHICH of the two hurdle sources produced the number beside it. The
    // old subtitle hard-coded the stop-buffer formula and kept claiming it long
    // after the hurdle started being measured from charges actually paid, so it
    // read "0.27% cost over 0.6% stop" next to a 0.147 that does not divide.
    hurdleBasisLabel() {
      if (!this.verdict) return "";
      if (this.verdict.hurdleBasis === "measured") {
        return "measured: charges paid over risk taken";
      }
      return "estimated: " + this.verdict.roundTripCostPct + "% cost over "
        + this.verdict.stopBufferPct + "% stop";
    },

    exitRows() {
      const breakdown = this.overall && this.overall.exitBreakdown;
      const total = this.overall && Number(this.overall.tradeCount);
      if (!breakdown || !total) return [];
      const LABELS = {
        stop: "Stopped out (never ratcheted)",
        eod_squareoff: "Squared off at EOD",
        trail_stop_hit: "Trailed out (win banked)",
        eod_close: "Held to close",
        target: "Target hit",
      };
      const ORDER = ["stop", "eod_squareoff", "trail_stop_hit", "eod_close", "target"];
      return Object.keys(breakdown)
        .sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))
        .map((reason) => ({
          reason,
          label: LABELS[reason] || reason,
          count: breakdown[reason],
          pct: ((breakdown[reason] / total) * 100).toFixed(1),
        }));
    },

    // The number that matters is how many trades RATCHETED AT ALL. In
    // trail_runner a trade that never reaches the first trigger cannot make
    // money by design, so this is the ceiling on the win rate.
    exitNote() {
      const breakdown = this.overall && this.overall.exitBreakdown;
      const total = this.overall && Number(this.overall.tradeCount);
      if (!breakdown || !total) return "";
      const ratcheted = Number(breakdown.trail_stop_hit || 0);
      const pct = ((ratcheted / total) * 100).toFixed(1);
      return `${pct}% of trades reached the first trail trigger. In trail_runner there is no `
        + "target exit, so a trade that never ratchets cannot bank a profit -- this is the ceiling "
        + "on the win rate, set by the entry rather than by the exit.";
    },

    statTiles() {
      const o = this.overall || {};
      return [
        { label: 'Trades', value: o.tradeCount ?? '—' },
        { label: 'Win rate', value: this.formatPct(o.winRatePct) },
        { label: 'Expectancy (net)', value: this.formatMoney(o.expectancyRs), cls: this.deltaClass(o.expectancyRs) },
        { label: 'Profit factor', value: this.isFiniteNumber(o.profitFactor) ? Number(o.profitFactor).toFixed(2) : '—' },
        { label: 'Net P&L', value: this.formatMoney(o.netPnlRs), cls: this.deltaClass(o.netPnlRs) },
        { label: 'Max drawdown', value: this.formatMoney(o.maxDrawdownRs), cls: 'text-error' },
      ];
    },
    // A day captured for many stocks but complete for few is the quiet failure
    // mode: the run silently skips the incomplete ones, so the sample is far
    // narrower than the capture count suggests.
    usableShortfall() {
      const any = Number(this.freshness.codesWithAnyCandles) || 0;
      const full = Number(this.freshness.codesWithFullSession) || 0;
      return any > 0 && full < any * 0.9;
    },
    freshnessWarning() {
      if (!this.freshness.latestDate) return 'No candle data stored yet.';
      const days = Math.floor((Date.now() - new Date(`${this.freshness.latestDate}T00:00:00`).getTime()) / 86400000);
      if (days > 4) return `Last captured ${days} days ago — results may not reflect recent sessions.`;
      if (this.usableShortfall) {
        return `Only ${this.freshness.codesWithFullSession} of ${this.freshness.codesWithAnyCandles} stocks have a complete session — the rest are skipped.`;
      }
      return null;
    },
  },
  created() {
    this.loadStrategies();
    this.loadFreshness();
    this.loadRuns();
  },
  beforeUnmount() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  methods: {
    // fetchWrapper rejects with a string or a status number, never an Error --
    // hence this idiom, used throughout the gap-backtest views.
    errText(err, fallback) {
      return typeof err === 'string' ? err : fallback;
    },
    isFiniteNumber(v) { return v !== null && v !== undefined && Number.isFinite(Number(v)); },
    formatPct(v) { return this.isFiniteNumber(v) ? `${Number(v).toFixed(1)}%` : '—'; },
    formatMoney(v) { return this.isFiniteNumber(v) ? this.$helper.formatPrice(Number(v)) : '—'; },
    fmtR(v) { return this.isFiniteNumber(v) ? `${Number(v).toFixed(3)} R` : '—'; },
    deltaClass(v) {
      if (!this.isFiniteNumber(v)) return '';
      return Number(v) >= 0 ? 'text-success' : 'text-error';
    },
    statusColor(s) {
      return { running: 'info', completed: 'success', failed: 'error', interrupted: 'warning' }[s] || 'grey';
    },

    loadStrategies() {
      fetchWrapper.get('api/strategy-settings')
        .then((res) => { this.strategies = res?.data || []; })
        .catch(() => { this.strategies = []; });
    },
    loadFreshness() {
      fetchWrapper.get('api/strategy-lab/data-freshness')
        .then((res) => { this.freshness = res?.data || {}; })
        .catch(() => { this.freshness = {}; });
    },
    loadRuns() {
      fetchWrapper.get('api/strategy-lab/runs')
        .then((res) => { this.runs = res?.data || []; })
        .catch(() => { this.runs = []; });
    },

    startRun() {
      // Parsed BEFORE the request so a typo surfaces here, rather than being
      // dropped and quietly producing a baseline run labelled as an experiment.
      this.extraConfigError = "";
      let parsedExtra = null;
      if (this.extraConfigText.trim()) {
        try {
          parsedExtra = JSON.parse(this.extraConfigText);
          if (typeof parsedExtra !== "object" || Array.isArray(parsedExtra)) throw new Error("not an object");
        } catch (err) {
          this.extraConfigError = "extra_config overrides must be a JSON object.";
          return;
        }
      }
      this.starting = true;
      this.startError = null;
      this.reviewMessage = null;
      fetchWrapper.post('api/strategy-lab/runs', {
        strategyType: this.form.strategyType,
        fromDate: this.form.fromDate,
        toDate: this.form.toDate,
        // Omitted entirely when off -- the API reads absent as "run uncapped",
        // and sending null would be indistinguishable from a bad value.
        ...(this.capEnabled ? { maxTradesPerDay: this.form.maxTradesPerDay } : {}),
        ...(this.guardEnabled ? { entryAfterMinutes: this.form.entryAfterMinutes } : {}),
        ...(Object.keys(this.cleanedOverrides).length ? { settingsOverrides: this.cleanedOverrides } : {}),
        ...(parsedExtra ? { extraConfigOverrides: parsedExtra } : {}),
      }).then((res) => {
        this.starting = false;
        const runId = res?.data?.runId;
        if (runId) this.viewRun(runId);
      }).catch((err) => {
        this.starting = false;
        this.startError = this.errText(err, 'Could not start the run.');
      });
    },

    guardLabel(run) {
      const snapshot = run && run.settings_snapshot;
      if (!snapshot) return "—";
      const guard = snapshot.entry_after_minutes;
      return guard === null || guard === undefined ? "off" : `${guard}m`;
    },

    // "off" and a number are different experiments, not a present/absent
    // value -- so an uncapped run says so rather than showing a dash that
    // reads like missing data.
    capLabel(run) {
      const snapshot = run && run.settings_snapshot;
      if (!snapshot) return "—";
      const cap = snapshot.max_trades_per_day;
      return cap === null || cap === undefined ? "off" : String(cap);
    },

    // silent: a background poll must never replace the view with an error
    // banner every few seconds (the pattern ParamOptimizer.vue uses).
    viewRun(runId, { silent = false } = {}) {
      // Clear any in-flight poll first. Without this, clicking Run or View
      // while a poll chain is alive starts a SECOND chain -- both then write
      // this.run every few seconds, and beforeUnmount only ever clears the last
      // timer set, so the other keeps firing after the page is gone.
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.pollTimer = null;

      fetchWrapper.get(`api/strategy-lab/runs/${encodeURIComponent(runId)}`)
        .then((res) => {
          const data = res?.data;
          if (!data) return;
          this.run = data;
          this.verdict = data.verdict;
          this.overall = data.overall_stats;

          if (data.presentedStatus === 'running') {
            // Deliberately NOT loading per-day rows here: they are written in
            // one go when the run completes, so polling them every 3s for
            // several minutes is a request per poll that can only return [].
            this.pollTimer = setTimeout(() => this.viewRun(runId, { silent: true }), 3000);
          } else {
            this.loadDays(runId);
            this.loadRuns();
          }
        })
        .catch((err) => {
          if (!silent) this.startError = this.errText(err, 'Could not load the run.');
          this.pollTimer = setTimeout(() => this.viewRun(runId, { silent: true }), 5000);
        });
    },

    loadDays(runId) {
      fetchWrapper.get(`api/strategy-lab/runs/${encodeURIComponent(runId)}/days`)
        .then((res) => { this.days = res?.data || []; })
        .catch(() => { this.days = []; });
    },

    markReviewed() {
      if (!this.run) return;
      this.reviewing = true;
      fetchWrapper.post(`api/strategy-lab/runs/${encodeURIComponent(this.run.strategy_lab_run_id)}/review`, {})
        .then((res) => {
          this.reviewing = false;
          this.reviewMessage = res?.message || 'Marked reviewed.';
          this.viewRun(this.run.strategy_lab_run_id, { silent: true });
        })
        .catch((err) => {
          this.reviewing = false;
          this.startError = this.errText(err, 'Could not mark it reviewed.');
        });
    },
  },
};
</script>
